import React, { useState, useEffect } from 'react';
import { api } from '../../api/nocode';
import { useAuth } from '../../context/AuthContext';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Textarea from '../../components/ui/Textarea';
import SafeIcon from '../../common/SafeIcon';
import AssemblyEditor from './content/AssemblyEditor';
import MessHallEditor from './content/MessHallEditor';
import CabinCallEditor from './content/CabinCallEditor';
import CampfireEditor from './content/CampfireEditor';
import HistoryModal from './content/HistoryModal';

const Accordion = ({ title, statusClass, isOpen, onClick, children }) => (
  <div className="border border-gray-200 rounded-md bg-white overflow-hidden shadow-sm">
    <button 
      className="w-full px-6 py-4 flex justify-between items-center bg-gray-50 hover:bg-gray-100 transition-colors"
      onClick={onClick}
    >
      <div className="flex items-center gap-3">
        <span className={`w-3 h-3 rounded-full ${statusClass}`}></span>
        <span className="font-bold text-lg text-gray-800">{title}</span>
      </div>
      <SafeIcon name={isOpen ? 'ChevronUp' : 'ChevronDown'} className="text-gray-500" />
    </button>
    {isOpen && <div className="p-6 border-t border-gray-200 bg-white">{children}</div>}
  </div>
);

export default function ContentManager() {
  const { user } = useAuth();
  const [activeDay, setActiveDay] = useState(1);
  const [content, setContent] = useState([]);
  const [globalSettings, setGlobalSettings] = useState({});
  const [originalSettings, setOriginalSettings] = useState([]);
  const [contentMode, setContentMode] = useState('live');
  
  const [openBlock, setOpenBlock] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLogs, setHistoryLogs] = useState([]);
  const [toast, setToast] = useState('');

  // Tools configuration state
  const [toolsButtons, setToolsButtons] = useState([]);

  const loadData = async () => {
    try {
      const tableName = contentMode === 'evergreen' ? 'content_evergreen_gwaacademy' : 'academy_content_gwaacademy';
      const [contentRes, settingsRes] = await Promise.all([
        api.get(tableName),
        api.get('settings_gwaacademy')
      ]);
      
      setContent(Array.isArray(contentRes) ? contentRes : contentRes.data || []);
      
      const setArray = Array.isArray(settingsRes) ? settingsRes : settingsRes.data || [];
      setOriginalSettings(setArray);
      
      const map = {};
      setArray.forEach(item => {
        if (item.setting_key) map[item.setting_key] = item.value;
      });
      setGlobalSettings(map);

      // Parse tools buttons JSON
      try {
        if (map.tools_buttons_json) {
          const parsed = JSON.parse(map.tools_buttons_json);
          setToolsButtons(Array.isArray(parsed) ? parsed : []);
        } else {
          setToolsButtons([]);
        }
      } catch (e) {
        setToolsButtons([]);
      }
    } catch (e) {
      console.error("Failed to load content", e);
    }
  };

  useEffect(() => {
    loadData();
  }, [contentMode]);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 4000);
  };

  const getBlockData = (blockName, day = activeDay) => {
    return content.find(c => c.day === day && c.block === blockName) || { day, block: blockName };
  };

  const getBlockStatusClass = (blockName, day = activeDay) => {
    const block = getBlockData(blockName, day);
    const isActive = block && (block.is_active == 1 || block.is_active === true || block.is_active === '1' || String(block.is_active).toLowerCase() === 'true');
    
    if (!block || !isActive) return 'bg-gray-400';
    
    const now = new Date();
    if (block.unlock_time && now < new Date(block.unlock_time)) return 'bg-gray-400';
    if (block.close_time && now > new Date(block.close_time)) return 'bg-amber';
    return 'bg-green-500';
  };

  const getDayStatusClass = (day) => {
    const blocks = ['assembly', 'messhall', 'cabincall', 'campfire'].map(b => getBlockStatusClass(b, day));
    if (blocks.some(s => s === 'bg-green-500')) return 'bg-green-500';
    if (blocks.every(s => s === 'bg-amber' || s === 'bg-gray-400')) return 'bg-amber';
    return 'bg-gray-400';
  };

  const logHistory = async (oldData, newData, blockId) => {
    const changes = [];
    Object.keys(newData).forEach(key => {
      if (['id', 'owner_id', 'day', 'block'].includes(key)) return;
      if (JSON.stringify(oldData?.[key]) !== JSON.stringify(newData[key])) {
        changes.push({
          field_name: key,
          old_value: oldData?.[key] ? String(oldData[key]) : '',
          new_value: newData[key] ? String(newData[key]) : ''
        });
      }
    });

    for (const change of changes) {
      await api.post('content_history_gwaacademy', {
        block_id: String(blockId),
        changed_by: user?.name || 'Admin',
        changed_at: new Date().toISOString(),
        ...change,
        owner_id: 'admin'
      });
    }
  };

  const handleSaveBlock = async (blockName, newData) => {
    try {
      const tableName = contentMode === 'evergreen' ? 'content_evergreen_gwaacademy' : 'academy_content_gwaacademy';
      const existing = getBlockData(blockName);
      const payload = { ...newData, day: activeDay, block: blockName, owner_id: 'admin' };
      
      if (typeof payload.is_active === 'boolean') {
        payload.is_active = payload.is_active ? 1 : 0;
      }

      let blockId = existing.id;

      if (existing.id) {
        await api.put(tableName, existing.id, payload);
      } else {
        const res = await api.post(tableName, payload);
        blockId = res?.data?.id || res?.id;
      }

      try {
        if (blockId) await logHistory(existing, payload, blockId);
      } catch (historyErr) {
        console.warn("Non-fatal: Failed to log history", historyErr);
      }

      showToast(`${blockName} saved successfully.`);
      loadData();
    } catch (e) {
      console.error("Save block err:", e);
      showToast(`Error saving: ${e.message}`);
    }
  };

  const handleViewHistory = async (blockName) => {
    const existing = getBlockData(blockName);
    if (!existing.id) {
      setHistoryLogs([]);
      setHistoryOpen(true);
      return;
    }
    
    try {
      const res = await api.get('content_history_gwaacademy', { block_id: existing.id });
      const logs = Array.isArray(res) ? res : res.data || [];
      setHistoryLogs(logs.sort((a, b) => new Date(b.changed_at) - new Date(a.changed_at)));
      setHistoryOpen(true);
    } catch (err) {
      console.error(err);
      setHistoryLogs([]);
      setHistoryOpen(true);
    }
  };

  const saveGlobalSettings = async () => {
    try {
      const payloadSettings = {
        ...globalSettings,
        tools_buttons_json: JSON.stringify(toolsButtons)
      };

      const promises = [];
      for (const [key, value] of Object.entries(payloadSettings)) {
        const orig = originalSettings.find(s => s.setting_key === key);
        const strValue = String(value ?? '');
        if (orig) {
          if (orig.value !== strValue) {
            promises.push(api.put('settings_gwaacademy', orig.id, { value: strValue, setting_key: key, owner_id: 'admin' }));
          }
        } else {
          if (strValue !== undefined && strValue !== '') {
            promises.push(api.post('settings_gwaacademy', { setting_key: key, value: strValue, owner_id: 'admin' }));
          }
        }
      }
      await Promise.all(promises);
      showToast('Global settings & event tools saved successfully.');
      await loadData();
    } catch (e) {
      showToast('Error saving settings: ' + e.message);
    }
  };

  const handleAddToolButton = () => {
    setToolsButtons(prev => [
      ...prev,
      {
        id: 'tool_' + Date.now(),
        label: '',
        link: '',
        description: ''
      }
    ]);
  };

  const handleUpdateToolButton = (index, field, value) => {
    setToolsButtons(prev => prev.map((tool, i) => i === index ? { ...tool, [field]: value } : tool));
  };

  const handleRemoveToolButton = (index) => {
    setToolsButtons(prev => prev.filter((_, i) => i !== index));
  };

  const bulkActivateToday = async () => {
    const blocks = ['assembly', 'messhall', 'cabincall', 'campfire'];
    for (const b of blocks) {
      const data = getBlockData(b);
      await handleSaveBlock(b, { ...data, is_active: 1 });
    }
  };

  const bulkDeactivateAll = async () => {
    if (!window.confirm("EMERGENCY SWITCH: This will hide ALL content across ALL days. Are you sure?")) return;
    const tableName = contentMode === 'evergreen' ? 'content_evergreen_gwaacademy' : 'academy_content_gwaacademy';
    for (const item of content) {
      await api.put(tableName, item.id, { ...item, is_active: 0 });
    }
    showToast('All blocks deactivated.');
    loadData();
  };

  const copyDay1Times = async () => {
    if (!window.confirm("Copy Day 1 unlock/close times to Days 2-5?")) return;
    
    const day1 = content.filter(c => c.day === 1);
    const tableName = contentMode === 'evergreen' ? 'content_evergreen_gwaacademy' : 'academy_content_gwaacademy';
    
    for (let d = 2; d <= 5; d++) {
      for (const b of ['assembly', 'messhall', 'cabincall', 'campfire']) {
        const source = day1.find(c => c.block === b);
        if (source && source.unlock_time) {
          const dest = getBlockData(b, d);
          
          const srcUnlock = new Date(source.unlock_time);
          const targetUnlock = new Date(srcUnlock);
          targetUnlock.setDate(targetUnlock.getDate() + (d - 1));
          
          const payload = {
            ...dest,
            unlock_time: targetUnlock.toISOString().slice(0, 16)
          };
          
          if (source.close_time) {
            const srcClose = new Date(source.close_time);
            const targetClose = new Date(srcClose);
            targetClose.setDate(targetClose.getDate() + (d - 1));
            payload.close_time = targetClose.toISOString().slice(0, 16);
          }
          
          if (dest.id) {
            await api.put(tableName, dest.id, payload);
          } else {
            await api.post(tableName, { ...payload, day: d, block: b, owner_id: 'admin' });
          }
        }
      }
    }
    showToast('Times copied to all days.');
    loadData();
  };

  const isToolsSectionActive = globalSettings.show_tools_section === 'true';

  return (
    <div className="space-y-6 max-w-6xl mx-auto pb-20">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-800">Content Manager</h1>
        <div className="flex bg-gray-100 p-1 rounded-md">
          <button
            onClick={() => setContentMode('live')}
            className={`px-4 py-2 text-sm font-bold rounded transition-colors ${contentMode === 'live' ? 'bg-white shadow text-dark-green' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Live Academy
          </button>
          <button
            onClick={() => setContentMode('evergreen')}
            className={`px-4 py-2 text-sm font-bold rounded transition-colors flex items-center gap-2 ${contentMode === 'evergreen' ? 'bg-white shadow text-forest-green' : 'text-gray-500 hover:text-gray-700'}`}
          >
            <SafeIcon name="Leaf" className="w-4 h-4" /> Evergreen
          </button>
        </div>
      </div>

      {toast && (
        <div className={`fixed top-4 right-4 text-white px-5 py-3 rounded-md shadow-lg z-50 animate-fadeIn ${toast.includes('Error') ? 'bg-red-600' : 'bg-dark-green'}`}>
          {toast}
        </div>
      )}

      {/* Bulk Actions */}
      <div className="bg-dark-green text-white p-4 rounded-md flex flex-col md:flex-row gap-4 items-center justify-between shadow-sm">
        <div className="font-bold flex items-center gap-2">
          <SafeIcon name="Layers" /> Bulk Actions
        </div>
        <div className="flex gap-2 flex-wrap justify-center">
          <Button variant="secondary" onClick={bulkActivateToday}>Activate All (Day {activeDay})</Button>
          <Button variant="outline" className="text-white border-white hover:bg-white hover:text-dark-green" onClick={bulkDeactivateAll}>Deactivate All</Button>
          <Button variant="ghost" className="text-white hover:bg-white/20" onClick={copyDay1Times}>Copy Day 1 Times</Button>
          <Button variant="ghost" className="text-white hover:bg-white/20" onClick={() => window.open(`/#/dashboard?preview=true&day=${activeDay}`, '_blank')}>
            <SafeIcon name="ExternalLink" className="mr-2" /> Preview as Student
          </Button>
        </div>
      </div>

      {/* Global Settings */}
      <Card>
        <h2 className="font-bold text-lg mb-4 text-dark-green flex items-center gap-2">
          <SafeIcon name="Globe" /> Global Content Settings
        </h2>
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <Input 
            label="Academy tagline" 
            value={globalSettings.camp_tagline || ''} 
            onChange={e => setGlobalSettings(p => ({ ...p, camp_tagline: e.target.value }))} 
          />
          <Input 
            label="Master Facebook Group URL" 
            value={globalSettings.global_fb_link || ''} 
            onChange={e => setGlobalSettings(p => ({ ...p, global_fb_link: e.target.value }))} 
          />
          <Input 
            label="Master Meetn Link" 
            value={globalSettings.global_zoom_link || ''} 
            onChange={e => setGlobalSettings(p => ({ ...p, global_zoom_link: e.target.value }))} 
          />
          <Input
            label="All-Access Pass Checkout URL"
            value={globalSettings.global_all_access_link || ''}
            onChange={e => setGlobalSettings(p => ({
              ...p,
              global_all_access_link: e.target.value
            }))}
            placeholder="https://gospelwiseauthor.com/checkout/..."
          />
        </div>
        <div className="space-y-4">
          <Textarea 
            label="Welcome message (Dashboard)" 
            value={globalSettings.welcome_message || ''} 
            onChange={e => setGlobalSettings(p => ({ ...p, welcome_message: e.target.value }))} 
          />
          <Textarea 
            label="Replay expiry message (Free Users)" 
            value={globalSettings.replay_expiry_msg || ''} 
            onChange={e => setGlobalSettings(p => ({ ...p, replay_expiry_msg: e.target.value }))} 
          />
          <Textarea 
            label="All-Access welcome message" 
            value={globalSettings.aa_welcome_msg || ''} 
            onChange={e => setGlobalSettings(p => ({ ...p, aa_welcome_msg: e.target.value }))} 
          />

          {/* DASHBOARD TOGGLES */}
          <div className="grid md:grid-cols-1 gap-4 mt-6">
            <div className="p-4 border rounded-md bg-gray-50 flex items-center justify-between">
              <div>
                <h4 className="font-bold text-dark-green">Today's Schedule block</h4>
                <p className="text-xs text-muted">Show the daily schedule block on the camper dashboard.</p>
              </div>
              <Button 
                variant={globalSettings.show_todays_schedule === 'true' ? 'primary' : 'outline'}
                onClick={() => setGlobalSettings(p => ({ ...p, show_todays_schedule: p.show_todays_schedule === 'true' ? 'false' : 'true' }))}
                className={globalSettings.show_todays_schedule === 'true' ? 'bg-forest-green text-white' : 'text-gray-500'}
              >
                {globalSettings.show_todays_schedule === 'true' ? 'ON' : 'OFF'}
              </Button>
            </div>
          </div>

          <Button onClick={saveGlobalSettings} className="mt-4">Save Global Settings</Button>
        </div>
      </Card>

      {/* EVENT RUN TOOLS SECTION SETTINGS */}
      <Accordion 
        title="Event Run Tools & Downloadable Resources (/tools)"
        statusClass={isToolsSectionActive ? 'bg-green-500' : 'bg-gray-400'}
        isOpen={openBlock === 'tools'}
        onClick={() => setOpenBlock(openBlock === 'tools' ? null : 'tools')}
      >
        <div className="space-y-6">
          <div className="flex justify-between items-center bg-gray-50 p-4 border rounded-md">
            <div>
              <h4 className="font-bold text-dark-green">Enable Tools Section & Sidebar Link</h4>
              <p className="text-xs text-muted">When ON, a "Tools" section appears in the student sidebar linking to /tools for this event run.</p>
            </div>
            <Button 
              variant={isToolsSectionActive ? 'primary' : 'outline'}
              onClick={() => setGlobalSettings(p => ({ ...p, show_tools_section: isToolsSectionActive ? 'false' : 'true' }))}
              className={isToolsSectionActive ? 'bg-forest-green text-white' : 'text-gray-500'}
            >
              {isToolsSectionActive ? 'ON' : 'OFF'}
            </Button>
          </div>

          <div className="space-y-4">
            <Input 
              label="Tools Page Headline"
              value={globalSettings.tools_headline || ''}
              onChange={e => setGlobalSettings(p => ({ ...p, tools_headline: e.target.value }))}
              placeholder="e.g. Event Tools & Author Resources"
            />
            <Textarea 
              label="Tools Page Description"
              value={globalSettings.tools_description || ''}
              onChange={e => setGlobalSettings(p => ({ ...p, tools_description: e.target.value }))}
              placeholder="Provide context and instructions for student users accessing these tools..."
            />
          </div>

          {/* Dynamic Tool Buttons */}
          <div className="border-t border-tan pt-6 space-y-4">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="font-bold text-dark-green text-lg flex items-center gap-2">
                  <SafeIcon name="Tool" /> Unique Tool Action Buttons
                </h3>
                <p className="text-xs text-muted">Add buttons for each tool, template, worksheet, or external software link.</p>
              </div>
              <Button onClick={handleAddToolButton} variant="secondary" className="text-sm">
                <SafeIcon name="Plus" className="mr-1" /> Add Tool Button
              </Button>
            </div>

            {toolsButtons.length === 0 ? (
              <div className="p-6 border border-dashed border-tan rounded-md text-center text-muted bg-gray-50">
                No tool buttons added yet. Click <b>"Add Tool Button"</b> above to add downloadable tools.
              </div>
            ) : (
              <div className="space-y-4">
                {toolsButtons.map((tool, index) => (
                  <div key={tool.id || index} className="p-5 border border-amber/40 bg-amber/5 rounded-md space-y-3 relative">
                    <div className="flex justify-between items-center border-b border-amber/20 pb-2">
                      <span className="font-bold text-sm text-dark-green">
                        Tool #{index + 1}
                      </span>
                      <button 
                        type="button"
                        onClick={() => handleRemoveToolButton(index)}
                        className="text-red-500 hover:text-red-700 text-xs font-semibold flex items-center gap-1"
                      >
                        <SafeIcon name="Trash2" /> Remove
                      </button>
                    </div>

                    <div className="grid md:grid-cols-2 gap-3">
                      <Input 
                        label="Button Label"
                        value={tool.label || ''}
                        onChange={e => handleUpdateToolButton(index, 'label', e.target.value)}
                        placeholder="e.g. StoryWise Outline Generator"
                      />
                      <Input 
                        label="Button Link / URL"
                        value={tool.link || ''}
                        onChange={e => handleUpdateToolButton(index, 'link', e.target.value)}
                        placeholder="https://example.com/download/tool.pdf or external app"
                      />
                    </div>
                    <Textarea 
                      label="Tool Description (Optional)"
                      value={tool.description || ''}
                      onChange={e => handleUpdateToolButton(index, 'description', e.target.value)}
                      placeholder="Brief note on what this tool does or how students should use it..."
                      className="min-h-[70px]"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          <Button onClick={saveGlobalSettings} className="bg-forest-green hover:bg-dark-green text-white w-full py-3 text-base shadow-sm">
            <SafeIcon name="Save" className="mr-2" /> Save Tools Settings &amp; Buttons
          </Button>
        </div>
      </Accordion>

      {/* CAMP ORIENTATION */}
      <Accordion 
        title="Academy Orientation (Dashboard)" 
        statusClass={globalSettings.show_camp_orientation !== 'false' ? 'bg-green-500' : 'bg-gray-400'}
        isOpen={openBlock === 'orientation'} 
        onClick={() => setOpenBlock(openBlock === 'orientation' ? null : 'orientation')}
      >
        <div className="space-y-4">
          <Input 
            label="Orientation Video Embed URL"
            value={globalSettings.orientation_video_url || ''}
            onChange={e => setGlobalSettings(p => ({ ...p, orientation_video_url: e.target.value }))}
            placeholder="https://livid.com/watch/..."
          />
          <Textarea 
            label="Welcome Text"
            value={globalSettings.orientation_welcome_text || ''}
            onChange={e => setGlobalSettings(p => ({ ...p, orientation_welcome_text: e.target.value }))}
            placeholder="Optional welcome message to display below the video..."
          />
          <div className="flex justify-between items-center bg-gray-50 p-4 border rounded-md mt-4">
            <div>
              <h4 className="font-bold text-dark-green">Academy Orientation Visibility</h4>
              <p className="text-xs text-muted">Show the orientation block on the camper dashboard before Day 1.</p>
            </div>
            <Button 
              variant={globalSettings.show_camp_orientation !== 'false' ? 'primary' : 'outline'}
              onClick={() => setGlobalSettings(p => ({ ...p, show_camp_orientation: p.show_camp_orientation !== 'false' ? 'false' : 'true' }))}
              className={globalSettings.show_camp_orientation !== 'false' ? 'bg-gwa-blue text-white' : 'text-gray-500'}
            >
              {globalSettings.show_camp_orientation !== 'false' ? 'ON' : 'OFF'}
            </Button>
          </div>
          <Button onClick={saveGlobalSettings} className="mt-4 bg-forest-green hover:bg-dark-green text-white">Save Orientation Settings</Button>
        </div>
      </Accordion>

      {/* Day Tabs */}
      <div className="flex flex-col md:flex-row gap-2 border-b border-gray-200 pb-2 mt-8">
        {[1, 2, 3, 4, 5].map(day => (
          <button
            key={day}
            onClick={() => { setActiveDay(day); setOpenBlock(null); }}
            className={`px-6 py-3 font-bold rounded-t-md transition-colors flex items-center justify-center gap-2 flex-1 md:flex-none
              ${activeDay === day ? 'bg-dark-green text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            Day {day}
            <span className={`inline-block w-2.5 h-2.5 rounded-full ${getDayStatusClass(day)}`}></span>
          </button>
        ))}
      </div>

      {/* Block Accordions */}
      <div className="space-y-4">
        <Accordion 
          title="Morning Lesson" 
          statusClass={getBlockStatusClass('assembly')}
          isOpen={openBlock === 'assembly'} 
          onClick={() => setOpenBlock(openBlock === 'assembly' ? null : 'assembly')}
        >
          <AssemblyEditor 
            data={getBlockData('assembly')} 
            onSave={(d) => handleSaveBlock('assembly', d)} 
            onHistory={() => handleViewHistory('assembly')}
          />
        </Accordion>

        <Accordion 
          title="Midday Lab" 
          statusClass={getBlockStatusClass('messhall')}
          isOpen={openBlock === 'messhall'} 
          onClick={() => setOpenBlock(openBlock === 'messhall' ? null : 'messhall')}
        >
          <MessHallEditor 
            data={getBlockData('messhall')} 
            globalZoom={globalSettings.global_zoom_link}
            onSave={(d) => handleSaveBlock('messhall', d)} 
            onHistory={() => handleViewHistory('messhall')}
          />
        </Accordion>

        <Accordion 
          title="Study Hall" 
          statusClass={getBlockStatusClass('cabincall')}
          isOpen={openBlock === 'cabincall'} 
          onClick={() => setOpenBlock(openBlock === 'cabincall' ? null : 'cabincall')}
        >
          <CabinCallEditor 
            data={getBlockData('cabincall')} 
            onSave={(d) => handleSaveBlock('cabincall', d)} 
            onHistory={() => handleViewHistory('cabincall')}
          />
        </Accordion>

        <Accordion 
          title="The Commons" 
          statusClass={getBlockStatusClass('campfire')}
          isOpen={openBlock === 'campfire'} 
          onClick={() => setOpenBlock(openBlock === 'campfire' ? null : 'campfire')}
        >
          <CampfireEditor 
            data={getBlockData('campfire')} 
            globalFb={globalSettings.global_fb_link}
            onSave={(d) => handleSaveBlock('campfire', d)} 
            onHistory={() => handleViewHistory('campfire')}
          />
        </Accordion>
      </div>

      <HistoryModal isOpen={historyOpen} onClose={() => setHistoryOpen(false)} logs={historyLogs} />
    </div>
  );
}